import { useEffect, useRef, useState } from "react";

import { fetchLeaderboard, submitScore } from "./api";
import type { Game as GameType } from "./engine/Game";
import {
  CAR_COLORS,
  CARS,
  type CarSpec,
  type LeaderboardEntry,
  useRace,
} from "./state";

const colorToHex = (s: string): number => parseInt(s.replace("#", ""), 16);

export type TouchAction = "left" | "right" | "accel" | "brake";

export function createBindTouch(emit: (action: TouchAction, down: boolean) => void) {
  const attached = new WeakMap<
    HTMLButtonElement,
    Array<[keyof HTMLElementEventMap, (e: Event) => void, AddEventListenerOptions | undefined]>
  >();

  return (action: TouchAction) =>
    (el: HTMLButtonElement | null) => {
      if (!el) return;
      if (attached.has(el)) return;

      const down = (e: Event) => {
        e.preventDefault();
        emit(action, true);
      };
      const up = (e: Event) => {
        e.preventDefault();
        emit(action, false);
      };

      const pairs: Array<[keyof HTMLElementEventMap, (e: Event) => void, AddEventListenerOptions | undefined]> = [
        ["touchstart", down, { passive: false }],
        ["touchend", up, { passive: false }],
        ["touchcancel", up, { passive: false }],
        ["mousedown", down, undefined],
        ["mouseup", up, undefined],
        ["mouseleave", up, undefined],
      ];
      for (const [type, h, opts] of pairs) {
        el.addEventListener(type, h as EventListener, opts);
      }
      attached.set(el, pairs);

      // Detach when the element leaves the DOM. Observing document.body for
      // subtree mutations + isConnected check handles React unmount, parent
      // removal, and node replacement uniformly.
      const obs = new MutationObserver(() => {
        if (!el.isConnected) {
          for (const [type, h] of pairs) {
            el.removeEventListener(type, h as EventListener);
          }
          attached.delete(el);
          obs.disconnect();
        }
      });
      obs.observe(document.body, { childList: true, subtree: true });
    };
}

export function App() {
  const phase = useRace((s) => s.phase);
  if (phase === "menu") return <Menu />;
  if (phase === "race") return <Race />;
  return <Result />;
}

// Best-effort: try to enter fullscreen + lock portrait DIRECTLY from a user
// gesture (Start button onClick). TG WebView gates `requestFullscreen` on
// gesture context; calling it from React's useEffect is too late. We don't
// gate on `isVersionAtLeast('8.0')` — the try/catch handles old clients.
function tryGoFullscreen(): void {
  type TgWebApp = {
    requestFullscreen?: () => void;
    lockOrientation?: (o: string) => void;
  };
  const tg = (window as unknown as { Telegram?: { WebApp?: TgWebApp } }).Telegram?.WebApp;
  if (!tg) return;
  try {
    tg.requestFullscreen?.();
  } catch (e) {
    console.warn("[tg] requestFullscreen failed:", e);
  }
  try {
    tg.lockOrientation?.("portrait");
  } catch {
    /* iOS WKWebView ignores; non-fatal */
  }
}

function Menu() {
  const { carId, color, setCar, setColor, startRace } = useRace();
  const onStart = () => {
    tryGoFullscreen();
    startRace();
  };
  return (
    <div className="stage">
      <div className="menu">
        <h1>Neon Race</h1>
        <p className="sub">Обери авто та колір</p>

        <div className="car-select" role="radiogroup" aria-label="Авто">
          {CARS.map((c) => {
            const selected = carId === c.id;
            // Selected car previews in player's chosen color; others render
            // in their signature accent so cards look distinct on the menu.
            const previewColor = selected ? color : c.accent;
            return (
              <button
                key={c.id}
                type="button"
                role="radio"
                aria-checked={selected}
                className={`car-option ${selected ? "selected" : ""}`}
                onClick={() => setCar(c.id)}
              >
                <CarPreview color={previewColor} />
                <div className="car-name">{c.name}</div>
                <div className="car-stat">
                  SPD {c.maxSpeed} · HND {c.handling.toFixed(1)}
                </div>
              </button>
            );
          })}
        </div>

        <div className="color-select" role="radiogroup" aria-label="Колір">
          {CAR_COLORS.map((c) => (
            <button
              key={c}
              type="button"
              role="radio"
              aria-checked={color === c}
              aria-label={`Колір ${c}`}
              className={`color-dot ${color === c ? "selected" : ""}`}
              style={{ background: c }}
              onClick={() => setColor(c)}
            />
          ))}
        </div>

        <button type="button" className="btn" onClick={onStart}>
          Старт
        </button>
      </div>
    </div>
  );
}

function CarPreview({ color }: { color: string }) {
  return (
    <svg width={48} height={64} viewBox="0 0 48 64" aria-hidden="true">
      <rect x={2} y={2} width={44} height={60} rx={8} fill={color} />
      <rect x={8} y={14} width={32} height={28} rx={4} fill="rgba(0,0,0,0.5)" />
      <rect x={2} y={56} width={44} height={6} fill="rgba(255,255,255,0.4)" />
    </svg>
  );
}

function Race() {
  const canvasWrapRef = useRef<HTMLDivElement | null>(null);
  const gameRef = useRef<GameType | null>(null);
  const { carId, color, finishRace } = useRace();
  const [hud, setHud] = useState<{ speed: number; pct: number; place: number; total: number; nitroActive: boolean }>({
    speed: 0,
    pct: 0,
    place: 1,
    total: 4,
    nitroActive: false,
  });
  const [stage, setStage] = useState<"loading" | "running" | "error">("loading");
  const [errMsg, setErrMsg] = useState<string>("");

  useEffect(() => {
    const carSpec: CarSpec = CARS.find((c) => c.id === carId) ?? CARS[0]!;
    let cancelled = false;
    let game: GameType | null = null;
    let pixiApp: import("pixi.js").Application | null = null;

    (async () => {
      try {
        console.info("[race] loading pixi.js…");
        // Telegram WebView CSP blocks 'unsafe-eval'; pixi.js by default uses
        // new Function() for text/shader compilation. The /unsafe-eval
        // entrypoint registers non-eval implementations.
        await import("pixi.js/unsafe-eval");
        const Pixi = await import("pixi.js");
        const { Application } = Pixi;
        const { Game } = await import("./engine/Game");
        if (cancelled) return;
        console.info("[race] pixi loaded, initing…");

        pixiApp = new Application();
        await pixiApp.init({
          width: 540,
          height: 960,
          backgroundColor: 0x0a0a18,
          backgroundAlpha: 1,
          antialias: true,
          resolution: Math.min(window.devicePixelRatio || 1, 2),
          autoDensity: true,
          preference: "webgl",
        });
        if (cancelled) {
          pixiApp.destroy(true);
          return;
        }
        console.info("[race] pixi init OK, renderer=", pixiApp.renderer.type);

        const wrap = canvasWrapRef.current;
        if (!wrap) {
          throw new Error("canvas-wrap ref is null after init");
        }
        wrap.appendChild(pixiApp.canvas);
        console.info("[race] canvas appended", pixiApp.canvas.width, "x", pixiApp.canvas.height);

        game = new Game(
          pixiApp,
          carSpec,
          colorToHex(color),
          {
            onTick: ({ speed, positionPct, place, total, nitroActive }) =>
              setHud({ speed, pct: positionPct, place, total, nitroActive }),
            onFinish: ({ position, timeMs }) => finishRace(position, timeMs),
          },
          { Application: Pixi.Application, Container: Pixi.Container, Graphics: Pixi.Graphics },
        );
        game.start();
        gameRef.current = game;
        if (!cancelled) setStage("running");
        console.info("[race] game.start() called, ticker running");
      } catch (err) {
        console.error("[race] init failed:", err);
        if (!cancelled) {
          setStage("error");
          setErrMsg(err instanceof Error ? err.message : String(err));
        }
      }
    })();

    const onKey = (e: KeyboardEvent, down: boolean) => {
      const g = gameRef.current;
      if (!g) return;
      if (e.key === "ArrowLeft" || e.key === "a" || e.key === "A") g.setInput({ left: down });
      else if (e.key === "ArrowRight" || e.key === "d" || e.key === "D")
        g.setInput({ right: down });
      else if (e.key === "ArrowUp" || e.key === "w" || e.key === "W") g.setInput({ accel: down });
      else if (e.key === "ArrowDown" || e.key === "s" || e.key === "S")
        g.setInput({ brake: down });
    };
    const keyDown = (e: KeyboardEvent) => onKey(e, true);
    const keyUp = (e: KeyboardEvent) => onKey(e, false);
    window.addEventListener("keydown", keyDown);
    window.addEventListener("keyup", keyUp);

    return () => {
      cancelled = true;
      window.removeEventListener("keydown", keyDown);
      window.removeEventListener("keyup", keyUp);
      if (game) game.destroy();
      if (pixiApp) {
        try {
          pixiApp.destroy(true);
        } catch {
          /* already destroyed */
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Fullscreen + lockOrientation are NOT called here — they require a
    // user-gesture context which only the Menu's Start button onClick has.
    // See tryGoFullscreen() near Menu.
    const tg = (
      window as unknown as {
        Telegram?: {
          WebApp?: {
            enableClosingConfirmation?: () => void;
            disableClosingConfirmation?: () => void;
            BackButton?: {
              show?: () => void;
              hide?: () => void;
              onClick?: (cb: () => void) => void;
              offClick?: (cb: () => void) => void;
            };
            onEvent?: (n: string, cb: (e: unknown) => void) => void;
            offEvent?: (n: string, cb: (e: unknown) => void) => void;
          };
        };
      }
    ).Telegram?.WebApp;
    if (!tg) return;

    tg.enableClosingConfirmation?.();

    const onBack = () => {
      finishRace(4, 0);
    };
    tg.BackButton?.show?.();
    tg.BackButton?.onClick?.(onBack);

    const onFsFailed = (e: unknown) => console.info("[tg] fullscreenFailed", e);
    tg.onEvent?.("fullscreenFailed", onFsFailed);

    return () => {
      tg.disableClosingConfirmation?.();
      tg.BackButton?.offClick?.(onBack);
      tg.BackButton?.hide?.();
      tg.offEvent?.("fullscreenFailed", onFsFailed);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const bindTouch = createBindTouch((action, down) => {
    gameRef.current?.setInput({ [action]: down });
  });

  return (
    <div className="stage">
      <div className={`canvas-wrap ${hud.nitroActive ? "nitro" : ""}`} ref={canvasWrapRef}>
        {stage === "running" && (
          <>
            <div className="hud">
              <div className="hud-box">
                МІСЦЕ <span className="v">{hud.place}</span> / {hud.total}
              </div>
              <div className="hud-box">
                ШВИДКІСТЬ <span className="v">{Math.round(hud.speed)}</span>
              </div>
              <div className="hud-box">
                <span className="v">{Math.round(hud.pct * 100)}%</span>
              </div>
            </div>
            <div className="touch-controls">
              <div className="m-group">
                <button
                  className="m-btn"
                  type="button"
                  aria-label="Ліворуч"
                  ref={bindTouch("left")}
                >
                  ◀
                </button>
                <button
                  className="m-btn"
                  type="button"
                  aria-label="Праворуч"
                  ref={bindTouch("right")}
                >
                  ▶
                </button>
              </div>
              <div className="m-group">
                <button
                  className="m-btn"
                  type="button"
                  aria-label="Гальмо"
                  ref={bindTouch("brake")}
                >
                  ⏸
                </button>
                <button
                  className="m-btn"
                  type="button"
                  aria-label="Газ"
                  ref={bindTouch("accel")}
                >
                  ▲
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      {stage === "loading" && (
        <div className="overlay">
          <div className="overlay-card">⏳ Завантажуємо двигун…</div>
        </div>
      )}
      {stage === "error" && (
        <div className="overlay">
          <div className="overlay-card">
            <strong>Не вдалось запустити гру.</strong>
            <div className="overlay-msg">{errMsg}</div>
            <p>Спробуй оновити сторінку або відкрити в іншому браузері.</p>
          </div>
        </div>
      )}
    </div>
  );
}

function Result() {
  const {
    finalPosition,
    finalTimeMs,
    carId,
    backToMenu,
    leaderboard,
    setLeaderboard,
    loadingLb,
    setLoadingLb,
  } = useRace();

  useEffect(() => {
    let cancelled = false;
    setLoadingLb(true);
    (async () => {
      try {
        try {
          await submitScore(finalTimeMs, carId);
        } catch {
          /* ignore */
        }
        const entries: LeaderboardEntry[] = await fetchLeaderboard().catch(() => []);
        if (!cancelled) setLeaderboard(entries);
      } finally {
        if (!cancelled) setLoadingLb(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [finalTimeMs, carId, setLeaderboard, setLoadingLb]);

  const posClass =
    finalPosition === 1
      ? "gold"
      : finalPosition === 2
      ? "silver"
      : finalPosition === 3
      ? "bronze"
      : "";
  const timeFmt = formatTime(finalTimeMs);

  return (
    <div className="stage">
      <div className="menu result">
        <h2>Фініш</h2>
        <div className={`pos ${posClass}`}>{finalPosition}</div>
        <div className="time">{timeFmt}</div>

        <div className="lb">
          {loadingLb ? (
            <div className="lb-row">Завантажуємо лідерборд…</div>
          ) : leaderboard.length === 0 ? (
            <div className="lb-row">Лідерборд недоступний</div>
          ) : (
            leaderboard.slice(0, 10).map((e) => (
              <div key={`${e.rank}-${e.user_id}`} className={`lb-row ${e.is_me ? "me" : ""}`}>
                <span className="lb-rank">{e.rank}</span>
                <span>{e.car_id}</span>
                <span className="lb-time">{formatTime(e.time_ms)}</span>
              </div>
            ))
          )}
        </div>

        <button
          type="button"
          className="btn pink"
          onClick={backToMenu}
          style={{ marginTop: 24 }}
        >
          Ще раз
        </button>
      </div>
    </div>
  );
}

function formatTime(ms: number): string {
  const totalSec = ms / 1000;
  const m = Math.floor(totalSec / 60);
  const s = (totalSec - m * 60).toFixed(2);
  return `${m}:${s.padStart(5, "0")}`;
}
